// src/pages/VerifyCustomerEmail.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { isEmailSignInLink, completeSignInWithEmailLink, createOrUpdateCustomerProfile } from '../api/customerAuthService';
import { auth } from '../utils/firebase';
import '../styles/customerAuth.css';

function VerifyCustomerEmail() {
  const [status, setStatus] = useState('verifying');
  const [email, setEmail] = useState('');
  const [, setNeedEmail] = useState(false);
  const [error, setError] = useState('');

  const { currentUser, currentCustomer, salonId: contextSalonId } = useCustomerAuth();
  const { salonId: urlSalonId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get email from query params
  const queryEmail = searchParams.get('email');

  // Use the salonId from either the URL params or the context, with validation
  const salonId = (urlSalonId && urlSalonId !== ':salonId') ? urlSalonId : contextSalonId;

  // Additional check to ensure we have a valid salonId
  useEffect(() => {
    // If salonId is not valid, try to get it from localStorage
    if (salonId === ':salonId' || !salonId) {
      const storedSalonId = localStorage.getItem('salonId') || localStorage.getItem('customerSalonId');
      if (storedSalonId && storedSalonId !== ':salonId') {
        console.log("Using salonId from localStorage:", storedSalonId);
      }
    }
  }, [salonId]);

  // Get redirect path from location state or default to customer home with correct URL pattern
  const from = location.state?.from?.pathname ||
    `/BespokeBeauty/${(salonId !== ':salonId' && salonId) ? salonId : 'demo-salon'}/customer/home`;
  
  // Add debug logging
  console.log("VerifyCustomerEmail component rendered with:", {
    currentUser,
    urlSalonId,
    contextSalonId,
    queryEmail,
    status,
    url: window.location.href,
    isSignInLink: isEmailSignInLink()
  });
  
  // Handle navigation when auth context is ready
  useEffect(() => {
    if (currentUser && currentCustomer && status === 'success') {
      console.log("Auth context ready, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [currentUser, currentCustomer, status, from, navigate]);

  useEffect(() => {
    const verifyEmailLink = async () => {
      console.log("Starting email verification process");

      // Try to get email from query params, then localStorage
      const storedEmail = queryEmail || localStorage.getItem('emailForSignIn');
      console.log("Email to use for verification:", storedEmail);

      // Case 1: URL contains sign-in link params (oobCode, mode=signIn)
      if (isEmailSignInLink()) {
        console.log("Valid sign-in link detected");

        if (storedEmail) {
          try {
            console.log("Attempting to complete sign-in with:", { email: storedEmail, salonId });
            await completeSignInWithEmailLink(storedEmail, salonId);
            console.log("Sign-in completed successfully");
            setStatus('success');
            // Navigate directly after a short delay (don't wait for context refresh)
            setTimeout(() => navigate(from, { replace: true }), 1500);
            return;
          } catch (err) {
            console.error('Error completing sign-in:', err);
            // Fall through to check if user is already authenticated
          }
        } else {
          // If no email available, we need to ask the user
          console.log("No email found, requesting from user");
          setNeedEmail(true);
          setStatus('need-email');
          return;
        }
      }

      // Case 2: User is already authenticated (e.g. Firebase action handler
      // completed the sign-in, or signInWithEmailLink above succeeded but
      // threw on re-entry). Create the customer profile if needed.
      if (auth.currentUser && salonId) {
        console.log("User already authenticated, creating customer profile");
        try {
          await createOrUpdateCustomerProfile(auth.currentUser, salonId);
          console.log("Customer profile created/updated for authenticated user");
          setStatus('success');
          setTimeout(() => navigate(from, { replace: true }), 1500);
          return;
        } catch (profileErr) {
          console.error('Error creating customer profile:', profileErr);
        }
      }

      // Case 3: Nothing worked
      if (status === 'verifying') {
        console.error("Not a valid sign-in link and no authenticated user");
        setError('Invalid verification link. Please request a new link to sign in.');
        setStatus('error');
      }
    };

    // If user is already logged in with customer profile, redirect to dashboard
    if (currentUser && currentCustomer) {
      console.log("User already logged in, redirecting to:", from);
      navigate(from, { replace: true });
    } else {
      verifyEmailLink();
    }
  }, [currentUser, currentCustomer, salonId, navigate, queryEmail, from]);
  
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setStatus('verifying');

    try {
      console.log("Manually completing sign-in with:", { email, salonId });
      await completeSignInWithEmailLink(email, salonId);
      console.log("Manual sign-in completed successfully");
      setStatus('success');
      setTimeout(() => navigate(from, { replace: true }), 1500);
    } catch (err) {
      console.error('Error completing sign-in:', err);
      // Fallback: if user got authenticated, create profile directly
      if (auth.currentUser && salonId) {
        try {
          await createOrUpdateCustomerProfile(auth.currentUser, salonId);
          setStatus('success');
          setTimeout(() => navigate(from, { replace: true }), 1500);
          return;
        } catch (profileErr) {
          console.error('Error creating profile:', profileErr);
        }
      }
      setError('Failed to verify your email. Please try again.');
      setStatus('error');
    }
  };
  
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };
  
  return (
    <div className="customer-auth-container">
      <div className="customer-auth-card">
        {status === 'verifying' && (
          <div className="customer-auth-loading">
            <div className="spinner"></div>
            <h2>Verifying your email...</h2>
          </div>
        )}
        
        {status === 'success' && (
          <div className="verification-success">
            <div className="success-icon">✓</div>
            <h2>Email Verified!</h2>
            <p>You've been successfully logged in.</p>
            <p>Redirecting to your account...</p>
            <div className="spinner" style={{ marginTop: '16px' }}></div>
          </div>
        )}
        
        {status === 'need-email' && (
          <>
            <h2>Confirm Your Email</h2>
            
            {error && <div className="customer-auth-error">{error}</div>}
            
            <p>Please enter the email address you used to request the login link:</p>
            
            <form onSubmit={handleEmailSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Your email address"
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="customer-auth-button"
              >
                Verify Email
              </button>
            </form>
          </>
        )}
        
        {status === 'error' && (
          <div className="verification-error">
            <div className="error-icon">!</div>
            <h2>Verification Failed</h2>
            <p>{error || 'There was a problem verifying your email.'}</p>
            <button
              className="customer-auth-button"
              onClick={() => navigate(`/BespokeBeauty/${salonId}/customer/login`)}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyCustomerEmail;
